import { gql } from "@apollo/client";

const ACCOUNT = gql`
  fragment ConsoleAccountFields on ConsoleFinancialAccount {
    id
    accountHolderId
    name
    currency
    status
    readiness
    issuer
    accountType
    capabilities
    stale
    available
    balance {
      available
      pending
      outgoing
      creditLimit
      outstanding
      reportedAt
    }
  }
`;
export const COMPANY_ACCOUNTS = gql`
  ${ACCOUNT}
  query companyAccounts(
    $companyId: ID!
    $first: Int
    $after: String
    $filter: CompanyConsoleFilter
  ) {
    companyFinancialAccounts(
      filter: $filter
      companyId: $companyId
      first: $first
      after: $after
    ) {
      edges {
        node {
          ...ConsoleAccountFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
export const COMPANY_ACCOUNT = gql`
  ${ACCOUNT}
  query companyAccount($companyId: ID!, $financialAccountId: ID!) {
    companyFinancialAccount(
      companyId: $companyId
      financialAccountId: $financialAccountId
    ) {
      ...ConsoleAccountFields
    }
  }
`;
export const COMPANY_DASHBOARD = gql`
  ${ACCOUNT}
  query companyDashboard(
    $companyId: ID!
    $filter: CompanyConsoleFilter
    $timeZone: String!
  ) {
    companyDashboard(
      companyId: $companyId
      filter: $filter
      timeZone: $timeZone
    ) {
      currencies {
        currency
        settledSpend
        moneyIn
        moneyOut
        exceptions
        pendingHolds
        pendingAuthorizations
      }
      trend {
        date
        currency
        settledSpend
      }
      accounts {
        ...ConsoleAccountFields
      }
      partial
      startDate
      endDate
      asOf
      definition
      activeCards
      frozenCards
    }
  }
`;
export const COMPANY_JOURNAL = gql`
  query companyJournal(
    $companyId: ID!
    $financialAccountId: ID
    $transactionId: ID
    $filter: CompanyConsoleFilter
    $first: Int
    $after: String
  ) {
    companyJournal(
      companyId: $companyId
      financialAccountId: $financialAccountId
      transactionId: $transactionId
      filter: $filter
      first: $first
      after: $after
    ) {
      coverage
      throughSequence
      edges {
        node {
          id
          financialAccountId
          sequence
          type
          amount
          currency
          effectiveAt
          recordedAt
          transactionId
          lines {
            ledgerAccount
            currency
            debit
            credit
          }
          references {
            financialEventId
            authorizationId
            entryIds
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
export const COMPANY_RECONCILIATION = gql`
  query companyReconciliation($companyId: ID!, $financialAccountId: ID!) {
    companyReconciliation(
      companyId: $companyId
      financialAccountId: $financialAccountId
    ) {
      coverage
      currency
      source
      issuerStatus
      transactionStatus
      issuerCheckedAt
      transactionCheckedAt
      issuerThrough
      transactionThrough
      generation
      asOf
      balances
    }
  }
`;
export const COMPANY_DISCREPANCIES = gql`
  query companyDiscrepancies(
    $companyId: ID!
    $financialAccountId: ID!
    $first: Int
    $after: String
  ) {
    companyDiscrepancies(
      companyId: $companyId
      financialAccountId: $financialAccountId
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          boundary
          finding
          status
          subject
          firstSeenAt
          updatedAt
          detail
          posting
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
const REQUEST = gql`
  fragment ConsoleRequestFields on ConsoleRequest {
    id
    companyId
    startedAt
    durationMs
    method
    route
    operationName
    httpStatus
    errorCode
    actorId
    operationDigest
    operationRequestId
    resourceId
    resourceType
    replayed
  }
`;
export const COMPANY_REQUESTS = gql`
  ${REQUEST}
  query companyRequests(
    $companyId: ID!
    $filter: ConsoleRequestFilter
    $first: Int
    $after: String
  ) {
    companyRequests(
      companyId: $companyId
      filter: $filter
      first: $first
      after: $after
    ) {
      available
      edges {
        node {
          ...ConsoleRequestFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
export const COMPANY_REQUEST = gql`
  ${REQUEST}
  query companyRequest($companyId: ID!, $requestId: ID!) {
    companyRequest(companyId: $companyId, requestId: $requestId) {
      ...ConsoleRequestFields
      operation {
        id
        status
        step
        updatedAt
        retryAt
        attempts
      }
    }
  }
`;
export const COMPANY_EVENTS = gql`
  query companyEvents(
    $companyId: ID!
    $resourceId: ID
    $requestId: ID
    $startDate: Float
    $endDate: Float
    $first: Int
    $after: String
  ) {
    companyEvents(
      companyId: $companyId
      resourceId: $resourceId
      requestId: $requestId
      startDate: $startDate
      endDate: $endDate
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          type
          createdAt
          requestId
          resourceId
          resourceType
          transition
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const COMPANY_ROUTING = gql`
  query companyRoutingDetails($companyId: ID!, $financialAccountId: ID!) {
    companyRoutingDetails(
      companyId: $companyId
      financialAccountId: $financialAccountId
    ) {
      ach {
        accountNumber
        routingNumber
        bankName
        accountHolderName
      }
    }
  }
`;
export const COMPANY_JOURNAL_ENTRY = gql`
  query companyJournalEntry(
    $companyId: ID!
    $financialAccountId: ID!
    $sequence: Int!
  ) {
    companyJournalEntry(
      companyId: $companyId
      financialAccountId: $financialAccountId
      sequence: $sequence
    ) {
      id
      financialAccountId
      sequence
      type
      amount
      currency
      effectiveAt
      recordedAt
      transactionId
      lines {
        ledgerAccount
        currency
        debit
        credit
      }
      references {
        financialEventId
        authorizationId
        entryIds
      }
    }
  }
`;

export const COMPANY_EVENT = gql`
  query companyEvent($companyId: ID!, $eventId: ID!) {
    companyEvent(companyId: $companyId, eventId: $eventId) {
      id
      type
      createdAt
      requestId
      resourceId
      resourceType
      transition
    }
  }
`;

export const COMPANY_TRANSACTION_EXPORT = gql`
  query companyTransactionExport(
    $companyId: ID!
    $filter: CompanyConsoleFilter!
    $first: Int
    $after: String
  ) {
    companyTransactionExport(
      companyId: $companyId
      filter: $filter
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          accountId
          cardId
          type
          status
          amount
          currency
          transactionDate
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
